import React, { useState, useEffect } from 'react';
import { FiSend, FiChevronDown } from 'react-icons/fi';
import EmailService from '../../../services/EmailService';
import RecipientInput from './RecipientInput';

const ComposePanel = ({ accounts, defaultFromAccountId, preFill, onSent, onCancel }) => {
  const [fromAccountId, setFromAccountId] = useState(defaultFromAccountId || accounts?.[0]?.id);
  const [recipientEmails, setRecipientEmails] = useState([]);
  const [form, setForm] = useState({ subject: '', body: '' });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setForm({
      subject: preFill?.subject || '',
      body: preFill?.body || '',
    });
    setError('');
    // Pre-select the account that received the original email (reply/forward)
    if (preFill?.fromAccountId) setFromAccountId(preFill.fromAccountId);
  }, [preFill]);

  useEffect(() => {
    if (defaultFromAccountId) setFromAccountId(defaultFromAccountId);
  }, [defaultFromAccountId]);

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const fromAccount = accounts?.find((a) => a.id === fromAccountId) || accounts?.[0];
  const multipleAccounts = (accounts?.length || 0) > 1;

  const handleSend = async () => {
    if (recipientEmails.length === 0 || !form.subject || !form.body) {
      setError('Please fill in all fields.');
      return;
    }
    if (!fromAccount) { setError('No account selected.'); return; }

    setSending(true);
    setError('');
    try {
      if (fromAccount.type === 'gmail') {
        await EmailService.sendGmail(fromAccount.token, recipientEmails.join(','), form.subject, form.body);
      } else if (fromAccount.type === 'zoho') {
        await EmailService.sendZoho(fromAccount.token, recipientEmails.join(','), form.subject, form.body);
      } else {
        // TODO: wire up SMTP send via backend API using fromAccount.config
        throw new Error('SMTP sending not yet implemented');
      }
      setForm({ subject: '', body: '' });
      if (onSent) onSent();
    } catch {
      setError('Failed to send email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const initialEmails = preFill?.to
    ? preFill.to.split(',').map((e) => e.trim()).filter(Boolean)
    : [];

  return (
    <div className="ec-compose-panel">
      <div className="ec-compose-header">
        <span className="ec-compose-title">{preFill?.title || 'New Message'}</span>
      </div>

      <div className="ec-compose-fields">
        {/* From — always visible; dropdown only when multiple accounts */}
        <div className="ec-compose-field">
          <span className="ec-compose-label">From</span>
          {multipleAccounts ? (
            <div className="ec-compose-from-wrap">
              <select
                className="ec-compose-from-select"
                value={fromAccountId}
                onChange={(e) => setFromAccountId(e.target.value)}
              >
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.email} ({a.label})
                  </option>
                ))}
              </select>
              <FiChevronDown size={13} className="ec-compose-from-chevron" />
            </div>
          ) : (
            <span className="ec-compose-from-readonly">
              {fromAccount?.email || ''}
              <span className="ec-provider-badge" style={{ marginLeft: 6 }}>{fromAccount?.label}</span>
            </span>
          )}
        </div>

        <div className="ec-compose-field">
          <span className="ec-compose-label">To</span>
          <RecipientInput
            key={preFill?.to ?? 'compose'}
            initialEmails={initialEmails}
            onChange={setRecipientEmails}
          />
        </div>

        <div className="ec-compose-field">
          <span className="ec-compose-label">Subject</span>
          <input
            className="ec-compose-input"
            placeholder="Email subject"
            value={form.subject}
            onChange={handleChange('subject')}
          />
        </div>
      </div>

      <div className="ec-compose-body-wrap">
        <textarea
          className="ec-compose-textarea"
          placeholder="Write your message..."
          value={form.body}
          onChange={handleChange('body')}
        />
      </div>

      <div className="ec-compose-footer">
        {error && (
          <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 8 }}>{error}</div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          {onCancel && (
            <button className="ec-compose-cancel-btn" onClick={onCancel} disabled={sending}>
              Cancel
            </button>
          )}
          <button className="ec-compose-send-btn" onClick={handleSend} disabled={sending}>
            {sending
              ? <><div className="ec-spinner" style={{ borderTopColor: '#fff', borderColor: 'rgba(255,255,255,0.3)' }} /> Sending...</>
              : <><FiSend size={14} /> Send</>
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default ComposePanel;
